import React, { useState, useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  HiClock,
  HiEnvelope as HiMail,
  HiChatBubbleLeft as HiChat,
  HiPhone,
  HiGlobeAlt,
  HiCog,
  HiPaperClip,
  HiEllipsisHorizontal,
  HiChevronDown,
  HiEye,
  HiArrowUturnLeft as HiReply,
  HiUserPlus as HiUserAdd,
  HiCheck
} from 'react-icons/hi2';
import { Avatar } from '@/components/Avatar';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { RichTextRenderer } from '@/components/editor/RichTextRenderer';
import {
  TicketStatus,
  TicketPriority,
  TicketSource,
  type User,
  getTicketStatusString,
  getTicketPriorityString
} from '@/types/api';
import type { TicketRowProps } from '@/types/ui';

import { userService } from '@/services/userService';

/**
 * Enhanced Ticket Row Component - Table View Layout
 * Comprehensive table row with configurable columns and interactive elements
 */
export const TicketRow: React.FC<TicketRowProps> = ({
  ticket,
  selected = false,
  onSelect,
  onClick,
  onStatusChange,
  onPriorityChange,
  onAssignmentChange,
  visibleColumns,
  compact = false,
  showIndex = false,
  index,
  className,
  'data-testid': testId,
  ...props
}) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [availableAgents, setAvailableAgents] = useState<User[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const priorityDropdownRef = useRef<HTMLDivElement>(null);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const assignDropdownRef = useRef<HTMLDivElement>(null);

  // Load available agents when assign dropdown opens
  useEffect(() => {
    if (showAssignDropdown && availableAgents.length === 0 && !loadingAgents) {
      setLoadingAgents(true);
      userService.getAllAgents()
        .then(res => {
          if (res.success && res.data) {
            setAvailableAgents(res.data.data);
          }
        })
        .catch(err => console.error('Failed to load agents in TicketRow:', err))
        .finally(() => setLoadingAgents(false));
    }
  }, [showAssignDropdown, availableAgents.length, loadingAgents]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
      if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(event.target as Node)) {
        setShowPriorityDropdown(false);
      }
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        setShowActionsDropdown(false);
      }
      if (assignDropdownRef.current && !assignDropdownRef.current.contains(event.target as Node)) {
        setShowAssignDropdown(false);
      }
    };

    if (showStatusDropdown || showPriorityDropdown || showActionsDropdown || showAssignDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showStatusDropdown, showPriorityDropdown, showActionsDropdown, showAssignDropdown]);

  // Status color mapping
  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.Open: return 'info';
      case TicketStatus.InProgress: return 'warning'; 
      case TicketStatus.OnHold: return 'secondary';
      case TicketStatus.Resolved: return 'success';
      case TicketStatus.Closed: return 'secondary';
      default: return 'secondary';
    }
  };

  // Priority indicator with color coding
  const getPriorityIndicator = (priority: TicketPriority) => {
    const colorClasses = {
      [TicketPriority.Urgent]: 'bg-red-500',
      [TicketPriority.High]: 'bg-orange-500',
      [TicketPriority.Medium]: 'bg-yellow-500',
      [TicketPriority.Low]: 'bg-green-500'
    };

    return (
      <div className="flex items-center space-x-2">
        <div className={clsx('w-2 h-2 rounded-full', colorClasses[priority])} />
        <span className="text-sm font-medium">{getTicketPriorityString(priority)}</span>
      </div>
    );
  };

  // Source icon mapping
  const getSourceIcon = (source: TicketSource) => {
    const iconClass = "w-4 h-4 text-gray-500";
    switch (source) {
      case TicketSource.Email: return <HiMail className={iconClass} title="Email" />;
      case TicketSource.Chat: return <HiChat className={iconClass} title="Chat" />;
      case TicketSource.Phone: return <HiPhone className={iconClass} title="Phone" />;
      case TicketSource.WebForm: return <HiGlobeAlt className={iconClass} title="Web Form" />;
      case TicketSource.API: return <HiCog className={iconClass} title="API" />;
      default: return <HiGlobeAlt className={iconClass} title="Web Form" />;
    }
  };

  // Format date for display (converts UTC to local timezone)
  const formatDate = (dateString: string | undefined | null, includeTime = false) => {
    if (!dateString) return 'N/A';

    // Parse ISO date string safely without appending redundant 'Z' if timezone is present
    let date = new Date(dateString);
    if (isNaN(date.getTime()) && !dateString.endsWith('Z')) {
      date = new Date(`${dateString}Z`);
    }

    // Check if date is valid
    if (isNaN(date.getTime()) || date.getTime() === 0 || date.getFullYear() < 2000) {
      return 'N/A';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    if (diffMs > 0 && diffMs < 24 * 60 * 60 * 1000) {
      return formatDistanceToNow(date, { addSuffix: true });
    }

    return includeTime
      ? format(date, 'MMM d, yyyy h:mm a')
      : format(date, 'MMM d, yyyy');
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger row click if clicking on interactive elements
    if (
      (e.target as HTMLElement).closest('button') ||
      (e.target as HTMLElement).closest('input') ||
      (e.target as HTMLElement).closest('select') ||
      (e.target as HTMLElement).closest('[role="button"]')
    ) {
      return;
    }
    
    onClick?.(ticket);
  };

  const handleSelectToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onSelect?.(ticket.id, e.target.checked);
  };

  const handleStatusChange = (newStatus: TicketStatus) => {
    onStatusChange?.(ticket.id, newStatus);
    setShowStatusDropdown(false);
  };

  const handlePriorityChange = (newPriority: TicketPriority) => {
    onPriorityChange?.(ticket.id, newPriority);
    setShowPriorityDropdown(false);
  };

  const rowClasses = clsx(
    // Base styles
    'border-b',
    'border-gray-200',
    'hover:bg-gray-50',
    'transition-colors',
    'duration-150',

    // Selected state
    selected && 'bg-primary-50 border-primary-200',

    // Height variations
    compact ? 'h-12' : 'h-16',

    // Hover state
    'cursor-pointer',

    // Dropdown z-index fix - provide proper stacking context
    'dropdown-parent',
    (showStatusDropdown || showPriorityDropdown || showActionsDropdown) && 'dropdown-open',

    className
  );

  // Define all possible columns
  const columnRenderers = {
    select: () => (
      <td className="px-4 py-3 w-12 min-w-12">
        <input
          type="checkbox"
          checked={selected}
          onChange={handleSelectToggle}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          aria-label={`Select ticket ${ticket.ticketNumber || ticket.id}`}
        />
      </td>
    ),

    index: () => (
      <td className="px-4 py-3 w-16 min-w-16 text-sm text-gray-500 font-mono">
        {index !== undefined ? index + 1 : ''}
      </td>
    ),

    id: () => (
      <td className="px-4 py-3 w-40 min-w-40">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-primary-600 hover:text-primary-700">
            #{ticket.ticketNumber || ticket.id}
          </span>
          {getSourceIcon(ticket.source)}
        </div>
      </td>
    ),

    subject: () => (
      <td className="px-4 py-3 min-w-0 flex-1">
        <div className="flex items-start space-x-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {ticket.subject}
              </h3>
              {ticket.attachments && ticket.attachments.length > 0 && (
                <HiPaperClip className="w-4 h-4 text-gray-400 flex-shrink-0" title="Has attachments" />
              )}
            </div>
            {!compact && ticket.description && (
              <div className="text-sm text-gray-600 truncate">
                <RichTextRenderer
                  content={ticket.description}
                  htmlContent={ticket.descriptionHtml}
                  className="truncate"
                  maxLength={100}
                />
              </div>
            )}
          </div>
        </div>
      </td>
    ),

    requester: () => (
      <td className="px-4 py-3 w-48 min-w-48">
        <div className="flex items-center space-x-3">
          <Avatar
            name={ticket.requester.fullName}
            size="sm"
            src={ticket.requester.profileImageUrl}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {ticket.requester.fullName}
            </p>
            <p className="text-sm text-gray-500 truncate">
              {ticket.requester.email}
            </p>
          </div>
        </div>
      </td>
    ),

    company: () => (
      <td className="px-4 py-3 w-40 min-w-40">
        <div className="text-sm">
          <p className="font-medium text-gray-900 truncate">
            {ticket.company?.name || 'N/A'}
          </p>
        </div>
      </td>
    ),

    status: () => (
      <td className="px-4 py-3 w-32 min-w-32">
        <div className="relative" ref={statusDropdownRef}>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowStatusDropdown(!showStatusDropdown);
              setShowActionsDropdown(false); // Close other dropdown
            }}
            className="flex items-center space-x-2 hover:bg-gray-100 rounded px-2 py-1 transition-colors"
            type="button"
            aria-expanded={showStatusDropdown}
            aria-haspopup="true"
          >
            <Badge
              variant={getStatusColor(ticket.status)}
              size="sm"
            >
              {getTicketStatusString(ticket.status)}
            </Badge>
            <HiChevronDown className={clsx(
              "w-4 h-4 text-gray-400 transition-transform",
              showStatusDropdown && "rotate-180"
            )} />
          </button>

          {showStatusDropdown && (
            <div className="dropdown-menu top-full left-0 mt-1 min-w-36">
              {Object.values(TicketStatus).map((status) => (
                <button
                  key={status}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleStatusChange(status);
                  }}
                  className={clsx(
                    'block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors first:rounded-t-md last:rounded-b-md',
                    ticket.status === status && 'bg-gray-50 font-medium'
                  )}
                  type="button"
                >
                  {getTicketStatusString(status)}
                </button>
              ))}
            </div>
          )}
        </div>
      </td>
    ),

    priority: () => (
      <td className="px-4 py-3 w-32 min-w-32">
        <div className="relative inline-block" ref={priorityDropdownRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowPriorityDropdown(!showPriorityDropdown);
              setShowStatusDropdown(false);
              setShowActionsDropdown(false);
            }}
            className="flex items-center space-x-1.5 hover:bg-gray-100 rounded px-2 py-1 transition-colors"
            type="button"
            aria-expanded={showPriorityDropdown}
            aria-haspopup="true"
            title="Click to change priority"
          >
            {getPriorityIndicator(ticket.priority)}
            <HiChevronDown className={clsx(
              "w-3.5 h-3.5 text-gray-400 transition-transform",
              showPriorityDropdown && "rotate-180"
            )} />
          </button>

          {showPriorityDropdown && (
            <div className="dropdown-menu top-full left-0 mt-1 min-w-32">
              {Object.values(TicketPriority).map((priority) => (
                <button
                  key={priority}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePriorityChange(priority);
                  }}
                  className={clsx(
                    'block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors first:rounded-t-md last:rounded-b-md',
                    ticket.priority === priority && 'bg-gray-50 font-medium'
                  )}
                  type="button"
                >
                  {getPriorityIndicator(priority)}
                </button>
              ))}
            </div>
          )}
        </div>
      </td>
    ),

    assignedTo: () => (
      <td className="px-4 py-3 w-40 min-w-40">
        <div className="relative inline-block w-full" ref={assignDropdownRef}>
          {ticket.assignedTo ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAssignDropdown(!showAssignDropdown);
                setShowStatusDropdown(false);
                setShowPriorityDropdown(false);
                setShowActionsDropdown(false);
              }}
              className="flex items-center space-x-2 text-left hover:bg-gray-100 p-1 rounded transition-colors w-full"
              type="button"
              title="Click to reassign ticket"
            >
              <Avatar
                name={ticket.assignedTo.fullName}
                size="sm"
                src={ticket.assignedTo.profileImageUrl}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {ticket.assignedTo.fullName}
                </p>
              </div>
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAssignDropdown(!showAssignDropdown);
                setShowStatusDropdown(false);
                setShowPriorityDropdown(false);
                setShowActionsDropdown(false);
              }}
              className="text-sm text-gray-500 hover:text-primary-600 flex items-center space-x-1 p-1 rounded hover:bg-gray-50 transition-colors"
              type="button"
              title="Click to assign agent"
            >
              <HiUserAdd className="w-4 h-4" />
              <span>Assign</span>
            </button>
          )}

          {showAssignDropdown && (
            <div className="dropdown-menu top-full left-0 mt-1 min-w-48 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg z-50 py-1">
              {ticket.assignedTo && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAssignmentChange?.(ticket.id, null);
                    setShowAssignDropdown(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 font-medium border-b border-gray-100"
                  type="button"
                >
                  Unassign ticket
                </button>
              )}
              {loadingAgents ? (
                <div className="px-3 py-2 text-xs text-gray-400 italic">Loading agents...</div>
              ) : availableAgents.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400 italic">No agents available</div>
              ) : (
                availableAgents.map((agent) => (
                  <button
                    key={agent.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAssignmentChange?.(ticket.id, agent.id);
                      setShowAssignDropdown(false);
                    }}
                    className={clsx(
                      'block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors flex items-center space-x-2',
                      (ticket.assignedTo?.id === agent.id || ticket.assignedToId === agent.id) && 'bg-primary-50 font-medium text-primary-900'
                    )}
                    type="button"
                  >
                    <Avatar name={agent.fullName || `${agent.firstName} ${agent.lastName}`} size="xs" src={agent.profileImageUrl || agent.avatarUrl} />
                    <span className="truncate">{agent.fullName || `${agent.firstName} ${agent.lastName}`}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </td>
    ),

    createdAt: () => (
      <td className="px-4 py-3 w-36 min-w-36">
        <div className="text-sm text-gray-900">
          <p title={ticket.createdAt && new Date(ticket.createdAt).getTime() > 0 ? format(new Date(ticket.createdAt), 'PPp') : 'Invalid date'}>
            {formatDate(ticket.createdAt)}
          </p>
        </div>
      </td>
    ),

    updatedAt: () => (
      <td className="px-4 py-3 w-36 min-w-36">
        <div className="flex items-center space-x-1 text-sm text-gray-500">
          <HiClock className="w-4 h-4 text-gray-500" style={{ width: '16px', height: '16px', flexShrink: 0 }} />
          <span title={ticket.updatedAt && new Date(ticket.updatedAt).getTime() > 0 ? format(new Date(ticket.updatedAt), 'PPp') : 'Invalid date'}>
            {formatDate(ticket.updatedAt || ticket.createdAt)}
          </span>
        </div>
      </td>
    ),

    actions: () => (
      <td className="px-4 py-3 w-20 min-w-20 sticky right-0 bg-white z-10">
        <div className="flex items-center justify-end relative" ref={actionsDropdownRef}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowActionsDropdown(!showActionsDropdown);
              setShowStatusDropdown(false); // Close other dropdown
            }}
            className="p-1.5"
            aria-label={`Actions for ticket ${ticket.ticketNumber || ticket.id}`}
            aria-expanded={showActionsDropdown}
            aria-haspopup="true"
          >
            <HiEllipsisHorizontal className="w-4 h-4" />
          </Button>

          {showActionsDropdown && (
            <div className="dropdown-menu right-0 top-full mt-1 min-w-40">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClick?.(ticket);
                  setShowActionsDropdown(false);
                }}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 first:rounded-t-md transition-colors"
                type="button"
              >
                <div className="flex items-center space-x-2">
                  <HiEye className="w-4 h-4" />
                  <span>View Details</span>
                </div>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClick?.(ticket);
                  setShowActionsDropdown(false);
                }}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors"
                type="button"
              >
                <div className="flex items-center space-x-2">
                  <HiReply className="w-4 h-4" />
                  <span>Reply</span>
                </div>
              </button>
              {ticket.status !== TicketStatus.Resolved && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleStatusChange(TicketStatus.Resolved);
                    setShowActionsDropdown(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100 last:rounded-b-md transition-colors"
                  type="button"
                >
                  <div className="flex items-center space-x-2">
                    <HiCheck className="w-4 h-4" />
                    <span>Mark Resolved</span>
                  </div>
                </button>
              )}
            </div>
          )}
        </div>
      </td>
    )
  };

  // Default visible columns
  const defaultColumns = ['select', 'id', 'subject', 'requester', 'status', 'priority', 'assignedTo', 'updatedAt', 'actions'];
  let columnsToShow = visibleColumns.length > 0 ? visibleColumns : defaultColumns;

  // Always ensure actions column is last and present
  columnsToShow = columnsToShow.filter(col => col !== 'actions');
  columnsToShow.push('actions');

  return (
    <tr
      className={rowClasses}
      onClick={handleRowClick}
      data-testid={testId}
      role="row"
      tabIndex={0}
      aria-label={`Ticket ${ticket.ticketNumber || ticket.id}: ${ticket.subject}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(ticket);
        }
      }}
      {...props}
    >
      {showIndex && columnRenderers.index()}
      {columnsToShow.map((column) => {
        const renderer = columnRenderers[column as keyof typeof columnRenderers];
        return renderer ? <React.Fragment key={column}>{renderer()}</React.Fragment> : null;
      })}
    </tr>
  );
};
